<template>
  <validation-observer v-if="testData" ref="formObserver" v-slot="{ passes }" tag="div">
    <p class="text-3xl mb-3">
      Question #{{ questionNum }} of {{ testData.questionCount }}
    </p>
    <div v-if="!question" class="flex items-center justify-center py-10">
      <d-loader />
    </div>
    <div v-else>
      <p class="font-bold mb-5" v-text="question.content" />
      <div>
        <d-field name="Answer" class="flex-wrap">
          <div v-for="answer in question.answers" :key="answer.id" class="w-full">
            <d-radio v-model="computedSelectAnswer" class="mx-0 mb-2" :native-value="answer.id">
              {{ answer.content }}
            </d-radio>
          </div>
        </d-field>
      </div>
      <div v-if="testData.requiresPageNumbers" class="pt-8">
        <d-field label="Page Number">
          <d-input v-model="computedPageNumber" />
        </d-field>
      </div>
      <div v-if="testData.requiresCitations" class="pt-8">
        <d-field label="Citation">
          <d-input v-model="computedPageNumber" />
        </d-field>
      </div>
      <div v-if="saving" class="flex flex-wrap justify-center items-center py-16">
        <d-loader />
      </div>
      <div v-else-if="$route.query.fromSummary" class="flex flex-wrap justify-end py-16">
        <d-button @click="passes(goToSummaryPage)">
          Go Back to Summary
        </d-button>
      </div>
      <div v-else class="flex flex-wrap justify-between py-16">
        <d-button v-if="questionNum >1" class="mb-3 md:mb-0" @click="goToPrevQuestion">
          Previous Question
        </d-button>
        <div v-else /> <!-- Empty div for spacing -->
        <d-button v-if="questionNum < testData.questionCount" @click="passes(goToNextQuestion)">
          Next Question
        </d-button>
        <d-button v-else @click="passes(goToSummaryPage)">
          Next Step
        </d-button>
      </div>
    </div>
  </validation-observer>
</template>

<script>
  import axios from 'axios';
  import { createNamespacedHelpers } from 'vuex';
  const { mapGetters, mapActions } = createNamespacedHelpers('tests');

  export default {
    data() {
      return {
        saving: false,
      };
    },

    computed: {
      ...mapGetters([
        'getQuestionByOrderNumber',
        'testData',
      ]),

      questionNum() {
        return this.$route.params.questionNum;
      },

      question() {
        return this.getQuestionByOrderNumber(this.questionNum);
      },

      computedSelectAnswer: {
        get() {
          return this.question.selectedAnswerId;
        },
        set(val) {
          this.updateQuestionSelectAnswer({ questionNumber: this.questionNum, answer: val });
        },
      },

      computedPageNumber: {
        get() {
          return this.question.selectedPageNumber;
        },
        set(val) {
          this.updateQuestionPageNumber({ questionNumber: this.questionNum, answer: val });
        },
      },
    },

    watch: {
      testData(val) {
        if (val && val.currentQuestionNumber) {
          this.updateServerPageNumber();
        }
      },
    },

    methods: {
      ...mapActions([
        'updateQuestionSelectAnswer',
        'updateQuestionPageNumber',
      ]),

      async goToPrevQuestion() {
        this.saving = true;
        try {
          if (this.computedSelectAnswer || this.computedPageNumber) {
            await axios.post(`/api/tests/${this.testData.id}/questions/${this.question.id}/answer`, {
              answerId: this.computedSelectAnswer,
              pageNumber: this.computedPageNumber,
              orderNumber: this.question.orderNumber,
            });
          }
          await axios.post(`/api/tests/${this.testData.id}/increment-question?increment=-1`);
          this.$router.push({ name: 'question', params: { questionNum: Number.parseInt(this.questionNum, 10) - 1 } });
        } catch (error) {
          this.$toast.error('An error occurred trying to save your answer. Please reload this page and try again.');
        } finally {
          this.saving = false;
        }
      },

      async goToNextQuestion() {
        this.saving = true;
        try {
          await axios.post(`/api/tests/${this.testData.id}/questions/${this.question.id}/answer`, {
            answerId: this.computedSelectAnswer,
            pageNumber: this.computedPageNumber,
            orderNumber: this.question.orderNumber,
          });
          await axios.post(`/api/tests/${this.testData.id}/increment-question?increment=1`);
          this.$router.push({ name: 'question', params: { questionNum: Number.parseInt(this.questionNum, 10) + 1 } });
        } catch (error) {
          this.$toast.error('An error occurred trying to save your answer. Please reload this page and try again.');
        } finally {
          this.saving = false;
        }
      },

      async goToSummaryPage() {
        this.saving = true;
        try {
          await axios.post(`/api/tests/${this.testData.id}/questions/${this.question.id}/answer`, {
            answerId: this.computedSelectAnswer,
            pageNumber: this.computedPageNumber,
            orderNumber: this.question.orderNumber,
          });
          this.$router.push({ name: 'summary' });
        } catch (error) {
          this.$toast.error('An error occurred trying to load the summary for your test. Please reload this page and try again.');
        } finally {
          this.saving = false;
        }
      },

      updateServerPageNumber() {
        if (Number.parseInt(this.questionNum, 10) !== this.testData.currentQuestionNumber) {
          const incrementValue = this.questionNum - this.testData.currentQuestionNumber;
          axios.post(`/api/tests/${this.testData.id}/increment-question?increment=${incrementValue}`);
        }
      },
    },
  };
</script>